import React, { useEffect, useRef } from "react";
import styles from "./Sheet.module.css";

/**
 * Bottom sheet, from design/Purchase Flow.dc.html. Every overlay in the
 * purchase flow - login, pay, gates, confirmations - is this one panel with
 * different content. Locks page scroll, closes on Escape and on the scrim,
 * and keeps focus inside while open.
 */
export const Sheet = ({ open, onClose, label, children, dismissible = true }) => {
  const panelRef = useRef(null);
  const restoreRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    restoreRef.current = document.activeElement;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape" && dismissible) {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const nodes = panelRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), a[href], select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);

    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKey, true);
      restoreRef.current?.focus?.();
    };
  }, [open, onClose, dismissible]);

  if (!open) return null;

  return (
    <div
      className={styles.scrim}
      onMouseDown={(e) => {
        if (dismissible && e.target === e.currentTarget) onClose?.();
      }}
    >
      <div ref={panelRef} className={styles.panel} role="dialog" aria-modal="true" aria-label={label}>
        <div className={styles.grabber} aria-hidden="true" />
        {children}
      </div>
    </div>
  );
};

export default Sheet;
